// Pure URL-extraction/classification helpers behind LinkPreview. Moved verbatim out of
// LinkPreview.tsx so non-React modules (the static-geometry height estimators in
// lib/answerPlaceholderHeight, which must mirror the read-mode embed render gates EXACTLY)
// can share the same implementation without importing the component graph (React, scss,
// api, stores). LinkPreview.tsx re-exports everything from here, so component-side import
// sites are unchanged.
import { uniq } from 'lodash'
import * as linkify from 'linkifyjs'
import type { Filter } from 'components/filters/useStreamFilters'
import { extractKnovEmbedId, UUID_REGEX } from 'components/shared/knovLinkTargets'

// Helper to escape regex special characters
const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const runtimeGon = () => {
    const runtime = globalThis as any
    return runtime.gon || runtime.window?.gon || {}
}

// Build host pattern from gon.treechatValidHosts (fallback to hardcoded for initial load)
const buildHostPattern = () => {
    const gon = runtimeGon()
    const validHosts = gon.treechatValidHosts
    if (validHosts && Array.isArray(validHosts) && validHosts.length > 0) {
        return validHosts.map(escapeRegex).join('|')
    }
    // Fallback to hardcoded production hosts if gon not loaded yet
    // (window.gon?.host mirrors knovHostName in state/knovStore, read lazily so this module
    // stays importable outside the browser).
    const documentHost =
        typeof document !== 'undefined' && document.location?.host ? document.location.host : null
    const knovHostName = gon.host || 'app.knovigator.com'
    return [
        documentHost ? escapeRegex(documentHost) : null,
        knovHostName ? escapeRegex(knovHostName) : null,
        'knovigator\\.com',
        'treechat\\.ai',
        'treechat\\.com',
    ]
        .filter(Boolean)
        .join('|')
}

// Cached regexes - rebuilt when host pattern changes (when gon loads).
type HostRegexpCache = {
    hostPattern: string | null
    regexp: RegExp | null
}

const createHostRegexpCache = (): HostRegexpCache => ({
    hostPattern: null,
    regexp: null,
})

const cachedKnovLinkRegexp = createHostRegexpCache()
const cachedStreamLinkRegexp = createHostRegexpCache()
const cachedLeaderboardLinkRegexp = createHostRegexpCache()
const cachedTdpLinkRegexp = createHostRegexpCache()

const getCachedHostRegexp = (
    cache: HostRegexpCache,
    buildRegexp: (currentPattern: string) => RegExp,
) => {
    const currentPattern = buildHostPattern()
    if (cache.hostPattern !== currentPattern || !cache.regexp) {
        cache.hostPattern = currentPattern
        cache.regexp = buildRegexp(currentPattern)
    }
    return cache.regexp
}

const getKnovLinkRegexp = () => {
    return getCachedHostRegexp(
        cachedKnovLinkRegexp,
        currentPattern =>
            new RegExp(`^https?:\/\/(([a-zA-Z0-9-]+\.))?(${currentPattern})\/(quest|p|s|answer)\/`),
    )
}

const getStreamLinkRegexp = () => {
    return getCachedHostRegexp(
        cachedStreamLinkRegexp,
        currentPattern =>
            new RegExp(`^https?:\/\/(([a-zA-Z0-9-]+\.))?(${currentPattern})\/stream\/`),
    )
}

const getLeaderboardLinkRegexp = () => {
    return getCachedHostRegexp(
        cachedLeaderboardLinkRegexp,
        currentPattern =>
            new RegExp(
                `^https?:\/\/(([a-zA-Z0-9-]+\.))?(${currentPattern})\/modal\/leaderboards(?:[\/?#]|$)`,
            ),
    )
}

const getTdpLinkRegexp = () => {
    return getCachedHostRegexp(
        cachedTdpLinkRegexp,
        currentPattern =>
            new RegExp(
                `^https?:\/\/(([a-zA-Z0-9-]+\.))?(${currentPattern})\/modal\/tdp(?:[\/?#]|$)`,
            ),
    )
}

// Export getter functions so regexes rebuild with gon.treechatValidHosts when available
export { getKnovLinkRegexp, getStreamLinkRegexp, getLeaderboardLinkRegexp, getTdpLinkRegexp }
export const twitterRegexp = /^https?:\/\/(www\.)?(x|twitter)\.com\/[^\/]+\/status\/([0-9]+)/
export const youtubeRegexp =
    /^https?:\/\/([^/]+\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com)\//i
const HTTP_PREVIEW_URL_REGEX = /^https?:\/\//i
const EMBED_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

type PreviewItemConfig = {
    previewUrl: string
}

export type LeaderboardEmbedConfig = {
    range: '1d' | null
    date: string | null
    previewUrl: string
}

export type TdpEmbedConfig = {
    range: '1d'
    date: string
    view: 'tdp' | 'sentiment' | 'share_rewards'
    previewUrl: string
}

export type StreamEmbedConfig = {
    id: string
    type: 'stream'
    url: string
    slug?: string
    filter?: Filter
}

export type MarketEmbedParty = { id: string; name: string }
export type MarketEmbedView = 'listing' | 'offer' | 'sale'

/**
 * Receipt op written by Ordinals::MarketAnnouncementService. Static by design:
 * artwork and live market state resolve client-side from `origin`/`ordinalId`.
 * `beneficiary` is who upvalues on the card pay (listing → seller, offer →
 * bidder, sale → buyer), frozen at event time.
 */
export type MarketEmbedConfig = {
    view: MarketEmbedView
    orderId: string
    ordinalId: string
    origin: string
    priceSats: number
    txid: string | null
    eventAt: string | null
    beneficiary: MarketEmbedParty | null
    counterparty: MarketEmbedParty | null
    /** Sales only: 'ask' = buy-now, 'bid' = accepted offer. */
    saleVia: 'ask' | 'bid' | null
    saleNumber: number | null
    prevPriceSats: number | null
    previewUrl: string
}

export function isPreviewableUrl(value: unknown): value is string {
    return typeof value === 'string' && HTTP_PREVIEW_URL_REGEX.test(value)
}

export function isYoutubeUrl(value: unknown): boolean {
    if (typeof value !== 'string' || !value) return false

    try {
        const hostname = new URL(value).hostname.toLowerCase().replace(/^www\./, '')
        return (
            hostname === 'youtu.be' ||
            hostname === 'youtube.com' ||
            hostname.endsWith('.youtube.com') ||
            hostname === 'youtube-nocookie.com' ||
            hostname.endsWith('.youtube-nocookie.com')
        )
    } catch (e) {
        return youtubeRegexp.test(value)
    }
}

const YOUTUBE_VIDEO_ID_REGEX = /^[a-zA-Z0-9\-_]+$/
const YOUTUBE_VIDEO_PATH_PREFIXES = ['embed', 'shorts', 'live', 'v']

// Mirrors Url.youtube_video_id_from_address on the server: only real video URL
// shapes yield an id. Playlists, channels, and other YouTube pages return null
// so they render as generic link previews instead of a broken video embed.
export function getYoutubeVideoId(value: unknown): string | null {
    if (typeof value !== 'string' || !value || !isYoutubeUrl(value)) return null

    try {
        const parsed = new URL(value)
        const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '')
        const segments = parsed.pathname.split('/').filter(Boolean)

        let candidate: string | null = null
        if (hostname === 'youtu.be') {
            candidate = segments[0] || null
        } else if (segments[0] === 'watch') {
            candidate = parsed.searchParams.get('v') || segments[1] || null
        } else if (YOUTUBE_VIDEO_PATH_PREFIXES.includes(segments[0])) {
            // embed/videoseries is the playlist embed player, not a video
            candidate =
                segments[0] === 'embed' && segments[1] === 'videoseries'
                    ? null
                    : segments[1] || null
        }

        return candidate && YOUTUBE_VIDEO_ID_REGEX.test(candidate) ? candidate : null
    } catch (e) {
        return null
    }
}

export function isYoutubeVideoUrl(value: unknown): boolean {
    return getYoutubeVideoId(value) != null
}

// Stale cached answers can carry a bogus video_id for non-video YouTube URLs
// (the old server extraction captured e.g. "playlist"); when the address is
// available it is the source of truth for whether this url is a video.
export function getUrlModelYoutubeVideoId(urlModel: unknown): string | null {
    if (!urlModel || typeof urlModel !== 'object') return null

    const { address, video_id: videoId } = urlModel as Record<string, unknown>
    if (typeof address === 'string' && address) return getYoutubeVideoId(address)

    return typeof videoId === 'string' && videoId ? videoId : null
}

export function getLeaderboardEmbedPreviewKey(
    config: Pick<LeaderboardEmbedConfig, 'range' | 'date'>,
) {
    return `leaderboard-embed://${config.range || 'all'}/${config.date || 'latest'}`
}

export function getTdpEmbedPreviewKey(config: Pick<TdpEmbedConfig, 'range' | 'date' | 'view'>) {
    return `tdp-embed://${config.range}/${config.date}/${config.view}`
}

function normalizeLeaderboardEmbedConfig(value: unknown): LeaderboardEmbedConfig | null {
    if (!value || typeof value !== 'object') return null

    const config = value as Record<string, unknown>
    const range = config.range
    const date = config.date
    const normalizedRange = range === '1d' ? '1d' : null
    const normalizedDate = typeof date === 'string' && date.match(EMBED_DATE_REGEX) ? date : null
    if (!normalizedRange && !normalizedDate) return null

    return {
        range: normalizedRange,
        date: normalizedDate,
        previewUrl: getLeaderboardEmbedPreviewKey({
            range: normalizedRange,
            date: normalizedDate,
        }),
    }
}

function normalizeTdpEmbedConfig(value: unknown): TdpEmbedConfig | null {
    if (!value || typeof value !== 'object') return null

    const config = value as Record<string, unknown>
    const range = config.range
    const date = config.date
    const view = config.view
    if (range !== '1d' || typeof date !== 'string' || !date.match(EMBED_DATE_REGEX)) {
        return null
    }
    if (view !== 'tdp' && view !== 'sentiment' && view !== 'share_rewards') return null

    return {
        range,
        date,
        view,
        previewUrl: getTdpEmbedPreviewKey({ range, date, view }),
    }
}

export function getMarketEmbedPreviewKey(config: Pick<MarketEmbedConfig, 'view' | 'orderId'>) {
    return `market-embed://${config.view}/${config.orderId}`
}

const MARKET_EMBED_VIEWS: MarketEmbedView[] = ['listing', 'offer', 'sale']

function normalizeMarketEmbedParty(value: unknown): MarketEmbedParty | null {
    if (!value || typeof value !== 'object') return null

    const party = value as Record<string, unknown>
    if (typeof party.id !== 'string' || !party.id) return null
    return { id: party.id, name: typeof party.name === 'string' ? party.name : '' }
}

function normalizeMarketEmbedConfig(value: unknown): MarketEmbedConfig | null {
    if (!value || typeof value !== 'object') return null

    const config = value as Record<string, unknown>
    const view = config.view
    if (typeof view !== 'string' || !MARKET_EMBED_VIEWS.includes(view as MarketEmbedView)) {
        return null
    }
    const orderId = config.order_id
    const ordinalId = config.ordinal_id
    const origin = config.origin
    if (typeof orderId !== 'string' || !orderId) return null
    if (typeof ordinalId !== 'string' || !ordinalId) return null
    if (typeof origin !== 'string' || !origin) return null
    const priceSats = Number(config.price_sats)
    if (!Number.isFinite(priceSats) || priceSats <= 0) return null
    const saleNumber = Number(config.sale_number)
    const prevPriceSats = Number(config.prev_price_sats)

    return {
        view: view as MarketEmbedView,
        orderId,
        ordinalId,
        origin,
        priceSats,
        txid: typeof config.txid === 'string' && config.txid ? config.txid : null,
        eventAt: typeof config.event_at === 'string' && config.event_at ? config.event_at : null,
        beneficiary: normalizeMarketEmbedParty(config.beneficiary),
        counterparty: normalizeMarketEmbedParty(config.counterparty),
        saleVia: config.sale_via === 'bid' ? 'bid' : config.sale_via === 'ask' ? 'ask' : null,
        saleNumber: Number.isFinite(saleNumber) && saleNumber > 0 ? saleNumber : null,
        prevPriceSats: Number.isFinite(prevPriceSats) && prevPriceSats > 0 ? prevPriceSats : null,
        previewUrl: getMarketEmbedPreviewKey({ view: view as MarketEmbedView, orderId }),
    }
}

function getMarketEmbedFromDeltaInsert(insert: unknown): MarketEmbedConfig | null {
    if (!insert || typeof insert !== 'object') return null

    return normalizeMarketEmbedConfig((insert as Record<string, unknown>).market_embed)
}

function getMarketEmbedFromDeltaAttributes(attributes: unknown): MarketEmbedConfig | null {
    if (!attributes || typeof attributes !== 'object') return null

    return normalizeMarketEmbedConfig((attributes as Record<string, unknown>).market_embed)
}

function getTdpEmbedFromDeltaInsert(insert: unknown): TdpEmbedConfig | null {
    if (!insert || typeof insert !== 'object') return null

    return normalizeTdpEmbedConfig((insert as Record<string, unknown>).tdp_embed)
}

function getTdpEmbedFromDeltaAttributes(attributes: unknown): TdpEmbedConfig | null {
    if (!attributes || typeof attributes !== 'object') return null

    return normalizeTdpEmbedConfig((attributes as Record<string, unknown>).tdp_embed)
}

function getLeaderboardEmbedFromDeltaInsert(insert: unknown): LeaderboardEmbedConfig | null {
    if (!insert || typeof insert !== 'object') return null

    return normalizeLeaderboardEmbedConfig((insert as Record<string, unknown>).leaderboard_embed)
}

function getLeaderboardEmbedFromDeltaAttributes(
    attributes: unknown,
): LeaderboardEmbedConfig | null {
    if (!attributes || typeof attributes !== 'object') return null

    return normalizeLeaderboardEmbedConfig(
        (attributes as Record<string, unknown>).leaderboard_embed,
    )
}

function uniqPreviewItems<T extends string | PreviewItemConfig>(items: T[]): T[] {
    const seen = new Set<string>()
    return items.filter(item => {
        const key = typeof item === 'string' ? item : item.previewUrl
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

const SPECIAL_STREAM_CONFIGS: Record<string, { gonIdKey?: string; filter: Filter }> = {
    hodlocker: { gonIdKey: 'HODLOCKER_TEAM_ID', filter: { hodlocker: true } },
    twetch: { gonIdKey: 'TWETCH_TEAM_ID', filter: { twetch: true } },
}

function getStreamPathSegment(url: string): string | null {
    if (!url || typeof url !== 'string') return null
    try {
        const parsed = new URL(url)
        const segments = parsed.pathname.replace(/\/+$/, '').split('/').filter(Boolean)
        const streamIndex = segments.findIndex(segment => segment === 'stream')
        return streamIndex >= 0 ? segments[streamIndex + 1] || null : null
    } catch (e) {
        const stripped = url.split('?')[0].split('#')[0].replace(/\/+$/, '')
        const segments = stripped.split('/').filter(Boolean)
        const streamIndex = segments.findIndex(segment => segment === 'stream')
        return streamIndex >= 0 ? segments[streamIndex + 1] || null : null
    }
}

export function extractStreamEmbedConfig(url: string): StreamEmbedConfig | null {
    if (!url?.match(getStreamLinkRegexp())) return null

    const segment = getStreamPathSegment(url)
    if (!segment) return null

    const uuidMatch = segment.match(UUID_REGEX)
    if (uuidMatch) {
        const streamId = uuidMatch[0]
        return {
            id: streamId,
            type: 'stream',
            url,
            filter: { team_ids: [streamId] },
        }
    }

    const slug = segment.toLowerCase()
    const specialConfig = SPECIAL_STREAM_CONFIGS[slug]
    if (!specialConfig) return null

    const gonValue =
        specialConfig.gonIdKey && typeof gon !== 'undefined'
            ? (gon as Record<string, unknown>)[specialConfig.gonIdKey]
            : null
    const streamId = typeof gonValue === 'string' && gonValue ? gonValue : slug

    return {
        id: streamId,
        type: 'stream',
        url,
        slug,
        filter: specialConfig.filter,
    }
}

export function extractStreamEmbedConfigs(urls: string[] = []): StreamEmbedConfig[] {
    const seen = new Set<string>()
    return (urls || [])
        .map(url => extractStreamEmbedConfig(url))
        .filter((config): config is StreamEmbedConfig => {
            if (!config) return false
            const key = config.id || config.url
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
}

export function extractLeaderboardEmbedConfig(url: string) {
    try {
        if (!url?.match(getLeaderboardLinkRegexp())) return null

        const parsed = new URL(url)
        if (parsed.searchParams.get('embed') !== '1') return null

        const range = parsed.searchParams.get('range')
        const date = parsed.searchParams.get('date')
        return normalizeLeaderboardEmbedConfig({ range, date })
    } catch (e) {
        return null
    }
}

export function extractTdpEmbedConfig(url: string) {
    try {
        if (!url?.match(getTdpLinkRegexp())) return null

        const parsed = new URL(url)
        if (parsed.searchParams.get('embed') !== '1') return null

        const range = parsed.searchParams.get('range')
        const date = parsed.searchParams.get('date')
        const view = parsed.searchParams.get('view')
        return normalizeTdpEmbedConfig({ range, date, view })
    } catch (e) {
        return null
    }
}

const deltaCodeLiteralInfo = (ops: Array<Record<string, any>>) => {
    const codeBlockIndexes = new Set<number>()
    const ordinaryLineIndexes = new Set<number>()
    let currentLineIndexes = new Set<number>()
    let currentLineText = ''
    const interpretableLines: string[] = []

    ops.forEach((op, index) => {
        if (typeof op?.insert !== 'string') {
            if (op?.insert != null) {
                currentLineIndexes.add(index)
                currentLineText += ' '
            }
            return
        }

        let cursor = 0
        for (let characterIndex = 0; characterIndex < op.insert.length; characterIndex += 1) {
            if (op.insert[characterIndex] !== '\n') continue

            const lineText = op.insert.slice(cursor, characterIndex)
            if (lineText) {
                currentLineIndexes.add(index)
                currentLineText += op.attributes?.code ? ' '.repeat(lineText.length) : lineText
            }

            if (op.attributes?.['code-block']) {
                currentLineIndexes.forEach(lineIndex => codeBlockIndexes.add(lineIndex))
                interpretableLines.push('')
            } else {
                currentLineIndexes.forEach(lineIndex => ordinaryLineIndexes.add(lineIndex))
                interpretableLines.push(currentLineText)
            }
            currentLineIndexes = new Set<number>()
            currentLineText = ''
            cursor = characterIndex + 1
        }

        const remainingText = op.insert.slice(cursor)
        if (remainingText) {
            currentLineIndexes.add(index)
            currentLineText += op.attributes?.code
                ? ' '.repeat(remainingText.length)
                : remainingText
        }
    })

    currentLineIndexes.forEach(lineIndex => ordinaryLineIndexes.add(lineIndex))
    interpretableLines.push(currentLineText)

    const fullyProtectedCodeBlockIndexes = new Set(
        [...codeBlockIndexes].filter(index => !ordinaryLineIndexes.has(index)),
    )
    return {
        fullyProtectedCodeBlockIndexes,
        interpretableText: interpretableLines.join('\n'),
    }
}

// Posts call extractUrls on every render (Answer.render, contentEmbedLinks), and
// linkify plus URL categorization was a top render cost while a feed scrolls. The
// result depends only on the input (a body object from the shared answer cache, or
// a string), so remember it: objects by identity, strings in a bounded map. Callers
// get fresh arrays, so mutating a result cannot corrupt the remembered one.
const extractedUrlsByObject = new WeakMap<object, ReturnType<typeof computeExtractedUrls>>()
const extractedUrlsByString = new Map<string, ReturnType<typeof computeExtractedUrls>>()
const EXTRACTED_URLS_STRING_LIMIT = 500

function copyExtractedUrls(result: ReturnType<typeof computeExtractedUrls>) {
    const copy = { ...result }
    for (const key of Object.keys(copy)) {
        if (Array.isArray(copy[key])) copy[key] = [...copy[key]]
    }
    return copy
}

/** returns an object of shape {knovEmbedLinks: [], nonKnovLinks: [], streamEmbedLinks: []} */
export function extractUrls(input) {
    if (input && typeof input === 'object') {
        let result = extractedUrlsByObject.get(input)
        if (!result) {
            result = computeExtractedUrls(input)
            extractedUrlsByObject.set(input, result)
        }
        return copyExtractedUrls(result)
    }
    if (typeof input === 'string') {
        let result = extractedUrlsByString.get(input)
        if (!result) {
            result = computeExtractedUrls(input)
            if (extractedUrlsByString.size >= EXTRACTED_URLS_STRING_LIMIT) {
                extractedUrlsByString.delete(extractedUrlsByString.keys().next().value)
            }
            extractedUrlsByString.set(input, result)
        }
        return copyExtractedUrls(result)
    }
    return computeExtractedUrls(input)
}

function computeExtractedUrls(input) {
    const urls = {
        knovEmbedLinks: [],
        nonKnovLinks: [],
        streamEmbedLinks: [],
        leaderboardEmbedLinks: [],
        tdpEmbedLinks: [],
        // Delta-op only: sale drops are server-authored, so there is no URL form.
        marketEmbedLinks: [] as MarketEmbedConfig[],
    }

    // Helper function to categorize URLs
    const categorizeUrl = url => {
        const result = { ...urls }
        const knovRegex = getKnovLinkRegexp()
        const streamRegex = getStreamLinkRegexp()
        const knovEmbedId = extractKnovEmbedId(url)
        const leaderboardEmbed = extractLeaderboardEmbedConfig(url)
        const tdpEmbed = extractTdpEmbedConfig(url)
        if (url?.match(knovRegex) && knovEmbedId) {
            result.knovEmbedLinks = uniq([...result.knovEmbedLinks, url])
        } else if (leaderboardEmbed) {
            result.leaderboardEmbedLinks = uniq([...result.leaderboardEmbedLinks, url])
        } else if (tdpEmbed) {
            result.tdpEmbedLinks = uniq([...result.tdpEmbedLinks, url])
        } else if (url?.match(streamRegex) && extractStreamEmbedConfig(url)) {
            result.streamEmbedLinks = uniq([...result.streamEmbedLinks, url])
        } else if (isYoutubeVideoUrl(url)) {
            // YouTube videos render through AnswerYouTubePlayer, whose thumbnail is
            // derived from the video id. Non-video YouTube pages (playlists,
            // channels) fall through to the generic link preview below.
        } else if (!url?.match(knovRegex) && !url?.match(streamRegex)) {
            result.nonKnovLinks = uniq([...result.nonKnovLinks, url])
        }
        return result
    }

    if (!input) return urls

    // Check if input is delta_json (object with ops array)
    if (typeof input === 'object' && input.ops && Array.isArray(input.ops)) {
        // Extract URLs from delta_json format
        const extractedUrls = []
        const extractedLeaderboardEmbeds: LeaderboardEmbedConfig[] = []
        const extractedTdpEmbeds: TdpEmbedConfig[] = []
        const extractedMarketEmbeds: MarketEmbedConfig[] = []
        const { fullyProtectedCodeBlockIndexes, interpretableText } = deltaCodeLiteralInfo(
            input.ops,
        )

        input.ops.forEach((op, index) => {
            const isCodeLiteral = op.attributes?.code || fullyProtectedCodeBlockIndexes.has(index)
            const leaderboardEmbed =
                !isCodeLiteral &&
                (getLeaderboardEmbedFromDeltaInsert(op.insert) ||
                    getLeaderboardEmbedFromDeltaAttributes(op.attributes))
            if (leaderboardEmbed) {
                extractedLeaderboardEmbeds.push(leaderboardEmbed)
            }

            const tdpEmbed =
                !isCodeLiteral &&
                (getTdpEmbedFromDeltaInsert(op.insert) ||
                    getTdpEmbedFromDeltaAttributes(op.attributes))
            if (tdpEmbed) {
                extractedTdpEmbeds.push(tdpEmbed)
            }

            const marketEmbed =
                !isCodeLiteral &&
                (getMarketEmbedFromDeltaInsert(op.insert) ||
                    getMarketEmbedFromDeltaAttributes(op.attributes))
            if (marketEmbed) {
                extractedMarketEmbeds.push(marketEmbed)
            }

            // Check for link attributes
            if (!isCodeLiteral && isPreviewableUrl(op.attributes?.link)) {
                extractedUrls.push(op.attributes.link)
            }
        })

        // Also check visible insert text for URLs (for backward compatibility).
        const textUrls =
            linkify
                ?.find(interpretableText, {
                    validate: {
                        url: value => isPreviewableUrl(value),
                    },
                })
                ?.filter(({ href }) => isPreviewableUrl(href))
                ?.map(u => u?.href) || []
        extractedUrls.push(...textUrls)

        // Categorize all extracted URLs
        const categorizedUrls = uniq(extractedUrls).reduce((acc, url) => {
            const categorized = categorizeUrl(url)
            return {
                knovEmbedLinks: uniq([...acc.knovEmbedLinks, ...categorized.knovEmbedLinks]),
                nonKnovLinks: uniq([...acc.nonKnovLinks, ...categorized.nonKnovLinks]),
                streamEmbedLinks: uniq([...acc.streamEmbedLinks, ...categorized.streamEmbedLinks]),
                leaderboardEmbedLinks: uniq([
                    ...acc.leaderboardEmbedLinks,
                    ...categorized.leaderboardEmbedLinks,
                ]),
                tdpEmbedLinks: uniq([...acc.tdpEmbedLinks, ...categorized.tdpEmbedLinks]),
            }
        }, urls)
        return {
            ...categorizedUrls,
            leaderboardEmbedLinks: uniqPreviewItems([
                ...categorizedUrls.leaderboardEmbedLinks,
                ...extractedLeaderboardEmbeds,
            ]),
            tdpEmbedLinks: uniqPreviewItems([
                ...categorizedUrls.tdpEmbedLinks,
                ...extractedTdpEmbeds,
            ]),
            marketEmbedLinks: uniqPreviewItems(extractedMarketEmbeds),
        }
    }

    // Handle plain text input (existing logic)
    if (typeof input === 'string') {
        const knovRegex = getKnovLinkRegexp()
        const streamRegex = getStreamLinkRegexp()
        return linkify
            ?.find(input, {
                validate: {
                    url: value => isPreviewableUrl(value),
                },
            })
            ?.filter(({ href }) => isPreviewableUrl(href))
            ?.map(u => u?.href)
            ?.reduce(
                (
                    {
                        knovEmbedLinks,
                        nonKnovLinks,
                        streamEmbedLinks,
                        leaderboardEmbedLinks,
                        tdpEmbedLinks,
                        marketEmbedLinks,
                    },
                    url,
                ) => {
                    if (url?.match(knovRegex) && extractKnovEmbedId(url))
                        knovEmbedLinks = uniq([...knovEmbedLinks, url])
                    else if (extractLeaderboardEmbedConfig(url))
                        leaderboardEmbedLinks = uniq([...leaderboardEmbedLinks, url])
                    else if (extractTdpEmbedConfig(url))
                        tdpEmbedLinks = uniq([...tdpEmbedLinks, url])
                    else if (url?.match(streamRegex) && extractStreamEmbedConfig(url))
                        streamEmbedLinks = uniq([...streamEmbedLinks, url])
                    else if (isYoutubeVideoUrl(url)) {
                        // YouTube video thumbnails are derived by AnswerYouTubePlayer, not
                        // LinkPreview; non-video YouTube pages get generic previews.
                    } else if (!url?.match(knovRegex) && !url?.match(streamRegex))
                        nonKnovLinks = uniq([...nonKnovLinks, url])
                    return {
                        knovEmbedLinks,
                        nonKnovLinks,
                        streamEmbedLinks,
                        leaderboardEmbedLinks,
                        tdpEmbedLinks,
                        marketEmbedLinks,
                    }
                },
                urls,
            )
    }

    return urls
}
